import type { ShapeElementModel } from '@blocksuite/affine-model';

import type { RoughCanvas } from '../../../rough/canvas.js';
import type { Renderer } from '../../renderer.js';
import { type Colors, drawGeneralShape } from './utils.js';

export function ellipse(
  model: ShapeElementModel,
  ctx: CanvasRenderingContext2D,
  matrix: DOMMatrix,
  renderer: Renderer,
  rc: RoughCanvas,
  colors: Colors
) {
  const {
    seed,
    strokeWidth,
    filled,
    strokeStyle,
    roughness,
    rotate,
    shapeStyle,
  } = model;
  const [, , w, h] = model.deserializedXYWH;
  const renderOffset = Math.max(strokeWidth, 0) / 2;
  const renderWidth = Math.max(1, w - renderOffset * 2);
  const renderHeight = Math.max(1, h - renderOffset * 2);
  const cx = renderWidth / 2;
  const cy = renderHeight / 2;

  const { fillColor, strokeColor } = colors;

  ctx.setTransform(
    matrix
      .translateSelf(renderOffset, renderOffset)
      .translateSelf(cx, cy)
      .rotateSelf(rotate)
      .translateSelf(-cx, -cy)
  );
  
  // Cast model to check for extended properties like tags
  const anyModel = model as unknown as {
    tags?: Array<{ name: string; color?: string }>;
    textDisplay?: boolean;
  };

  if (shapeStyle === 'General') {
    drawGeneralShape(ctx, model, renderer, filled, fillColor, strokeColor);
  } else {
    rc.ellipse(cx, cy, renderWidth, renderHeight, {
      seed,
      roughness: shapeStyle === 'Scribbled' ? roughness : 0,
      strokeLineDash: strokeStyle === 'dash' ? [12, 12] : undefined,
      stroke: strokeStyle === 'none' ? 'none' : strokeColor,
      strokeWidth,
      fill: filled ? fillColor : undefined,
      curveFitting: 1,
    });
  }

  // Render image content if provided on the model (imageUrl or imageSvg)
  const modelWithImage: {
    imageUrl?: string;
    imageSvg?: string;
    __imgCache?: HTMLImageElement;
    __imgSrc?: string;
    isIcon?: boolean;
    textDisplay?: boolean;
  } = model as {
    imageUrl?: string;
    imageSvg?: string;
    __imgCache?: HTMLImageElement;
    __imgSrc?: string;
    isIcon?: boolean;
    textDisplay?: boolean;
  };

  const imgSrc =
    modelWithImage.imageUrl ??
    (modelWithImage.imageSvg
      ? `data:image/svg+xml;utf8,${encodeURIComponent(modelWithImage.imageSvg)}`
      : undefined);

  if (imgSrc) {
    if (!modelWithImage.__imgCache || modelWithImage.__imgSrc !== imgSrc) {
      const img = new Image();
      // Intentionally not setting crossOrigin to allow cross-origin avatars to load
      img.decoding = 'async';
      img.onload = () => {
        renderer.refresh();
      };
      img.src = imgSrc;
      modelWithImage.__imgCache = img;
      modelWithImage.__imgSrc = imgSrc;
    }

    const img = modelWithImage.__imgCache;
    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      ctx.save();

      // Clip to ellipse shape
      ctx.beginPath();
      ctx.ellipse(cx, cy, renderWidth / 2, renderHeight / 2, 0, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();

      // Check if this is an icon that should be displayed with text
      if (modelWithImage.isIcon && modelWithImage.textDisplay !== false) {
        // Icon mode: draw larger icon with tighter spacing for prettier effect
        const iconSize = Math.min(renderWidth * 0.35, renderHeight * 0.35, 25);
        const iconX = (renderWidth - iconSize) / 2;
        const iconY = renderHeight * 0.12;

        ctx.drawImage(img, iconX, iconY, iconSize, iconSize);
      } else {
        // Full image mode: draw the image stretched to the ellipse bounds
        ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
      }

      ctx.restore();
    }
  }
  
  // Render tags below text if provided
  if (anyModel.tags && anyModel.tags.length > 0 && anyModel.textDisplay !== false) {
    ctx.save();
    
    // Stack tags vertically for better appearance
    const tagHeight = Math.min(12, renderHeight * 0.08);
    const iconSize = tagHeight * 0.55;
    const iconPadding = iconSize * 0.9;
    const tagPadding = tagHeight * 0.35;
    const tagVerticalSpacing = tagHeight * 0.5;
    
    // Start tags at 65% down from the top (well below center text)
    let currentY = renderHeight * 0.65;
    
    ctx.font = `${tagHeight * 0.7}px sans-serif`;
    
    // Render up to 3 tags stacked vertically
    for (const tag of anyModel.tags.slice(0, 3)) {
      // Truncate with ellipsis if needed
      let tagText = tag.name;
      const maxChars = 10;
      if (tagText.length > maxChars) {
        tagText = tagText.slice(0, maxChars - 1) + '…';
      }
      
      const textMetrics = ctx.measureText(tagText);
      const maxTagWidth = renderWidth * 0.6;
      const tagWidth = Math.min(textMetrics.width + tagPadding * 2 + iconPadding + iconSize, maxTagWidth);
      
      // Center the tag horizontally
      const tagX = (renderWidth - tagWidth) / 2;
      
      // Pill-shaped tag with faint fill using globalAlpha
      const tagRadius = tagHeight * 0.5;
      
      // Set the base color once
      const baseColor = tag.color || 'rgba(139, 92, 246, 1)';
      
      // Very faint fill
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = baseColor;
      ctx.beginPath();
      ctx.roundRect(tagX, currentY, tagWidth, tagHeight, tagRadius);
      ctx.fill();
      
      // Border with full color
      ctx.globalAlpha = 1;
      ctx.strokeStyle = baseColor;
      ctx.lineWidth = 0.5;
      ctx.stroke();
      
      // Draw hashtag icon with full color
      ctx.globalAlpha = 1;
      ctx.strokeStyle = baseColor;
      ctx.lineWidth = iconSize * 0.1;
      ctx.lineCap = 'round';
      
      const iconX = tagX + tagPadding * 0.8;
      const iconY = currentY + (tagHeight - iconSize) / 2;
      
      // Draw hashtag symbol
      ctx.beginPath();
      // Vertical lines
      ctx.moveTo(iconX + iconSize * 0.35, iconY);
      ctx.lineTo(iconX + iconSize * 0.35, iconY + iconSize);
      ctx.moveTo(iconX + iconSize * 0.65, iconY);
      ctx.lineTo(iconX + iconSize * 0.65, iconY + iconSize);
      // Horizontal lines
      ctx.moveTo(iconX, iconY + iconSize * 0.35);
      ctx.lineTo(iconX + iconSize, iconY + iconSize * 0.35);
      ctx.moveTo(iconX, iconY + iconSize * 0.65);
      ctx.lineTo(iconX + iconSize, iconY + iconSize * 0.65);
      ctx.stroke();
      
      // Tag text - use the same color as the main element text
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tagText, tagX + tagWidth / 2 + iconSize * 0.25, currentY + tagHeight / 2);
      
      currentY += tagHeight + tagVerticalSpacing;
    }
    
    // Reset globalAlpha
    ctx.globalAlpha = 1;
    ctx.restore();
  }
}
